import { useState } from "react";
import { format, parse, isValid } from "date-fns";
import { fr, de, enGB } from "date-fns/locale";
import { CalendarIcon } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const LOCALES = { fr, de, en: enGB };

/**
 * Date picker using shadcn Calendar + Popover.
 * value: ISO string "YYYY-MM-DD" | "" | undefined
 * onChange: (iso) => void
 */
const PLACEHOLDER_I18N = { fr: "Sélectionner une date", de: "Datum auswählen", en: "Select a date" };

export default function DatePicker({
  value,
  onChange,
  placeholder,
  lang = "fr",
  minDate,
  className = "",
  triggerClassName = "",
  testId = "date-picker",
}) {
  const [open, setOpen] = useState(false);
  const locale = LOCALES[lang] || fr;
  const ph = placeholder || PLACEHOLDER_I18N[lang] || PLACEHOLDER_I18N.fr;
  const selected = value ? parse(value, "yyyy-MM-dd", new Date()) : undefined;
  const displayValue = selected && isValid(selected) ? format(selected, "EEEE d MMMM yyyy", { locale }) : "";
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const fromDate = minDate || today;

  return (
    <div className={className}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className={`w-full inline-flex items-center justify-between gap-2 rounded-xl border border-gray-200 bg-white px-4 py-3 text-left text-[#0A0F2C] hover:border-[#2D5BFF] focus:ring-2 focus:ring-[#2D5BFF] focus:border-transparent outline-none transition-colors ${triggerClassName}`}
            data-testid={`${testId}-trigger`}
          >
            <span className={`truncate ${displayValue ? "" : "text-gray-400"}`}>
              {displayValue || ph}
            </span>
            <CalendarIcon size={18} className="text-[#2D5BFF] shrink-0" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="p-0 w-auto rounded-2xl border border-gray-100 shadow-[0_20px_50px_-15px_rgba(10,15,44,0.25)]">
          <Calendar
            mode="single"
            selected={selected}
            onSelect={(d) => {
              onChange?.(d ? format(d, "yyyy-MM-dd") : "");
              setOpen(false);
            }}
            locale={locale}
            fromDate={fromDate}
            initialFocus
            data-testid={`${testId}-calendar`}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
